import { Plugin } from 'vue';
import createAuth0Client from '@auth0/auth0-spa-js';
import Auth0Client from '@auth0/auth0-spa-js/dist/typings/Auth0Client';
import config from '@/config/client';

export interface AppState {
  inviteToken: string;
  kickLease?: boolean;
  targetUrl: string;
}

interface LogoutOptions {
  returnTo: string;
}

const DEFAULT_REDIRECT_CALLBACK = (_appState: AppState) =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance = null;

/** Returns the current instance of the SDK */
export const getInstance = (): AuthPlugin => instance;

type AuthOptions = {
  onRedirectCallback: (appState: AppState) => void;
  redirectUri: string;
  clientId: string;
  domain: string;
  audience: string;
};

class AuthPlugin {
  auth0Client: Auth0Client;
  claims: { [key: string]: any };
  clientId: string;
  error: any;
  isAuthenticated: boolean = false;
  loading: boolean = true;
  popupOpen: boolean = false;
  token: string = '';
  user: { [key: string]: any } = {};
  targetUrl: string = null;
  idToken: string = null;
  options: AuthOptions = null;
  private loadListeners: Array<() => void> = [];

  constructor(options: AuthOptions) {
    this.options = options;

    this.init();
  }

  onLoaded(fn: () => void) {
    this.loadListeners.push(fn);
  }

  /** Authenticates the user using a popup window */
  async loginWithPopup(opt) {
    this.popupOpen = true;

    try {
      await this.auth0Client.loginWithPopup(opt);
    } catch (err) {
      // eslint-disable-next-line
      console.error(err);
    } finally {
      this.popupOpen = false;
    }

    this.user = await this.auth0Client.getUser();
    this.isAuthenticated = true;
  }

  /** Handles the callback when logging in using a redirect */
  // eslint-disable-next-line consistent-return
  async handleRedirectCallback() {
    try {
      const data = await this.auth0Client.handleRedirectCallback();
      this.user = await this.auth0Client.getUser();
      this.claims = await this.auth0Client.getIdTokenClaims();
      this.isAuthenticated = true;

      return data;
    } catch (err) {
      this.error = err;
    }
  }

  /** Authenticates the user using the redirect method */
  loginWithRedirect(opt) {
    return this.auth0Client.loginWithRedirect(opt);
  }

  /** Returns all the claims present in the ID token */
  getIdTokenClaims(opt = {}) {
    return this.auth0Client.getIdTokenClaims(opt);
  }

  /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
  getTokenSilently(opt = {}) {
    if (TESTING_UTIL && this.token) {
      // For e2e tests, return a token that has been set on our auth instance from localStorage
      return Promise.resolve(this.token);
    }

    return this.auth0Client.getTokenSilently(opt);
  }

  /** Gets the access token using a popup window */
  getTokenWithPopup(opt) {
    return this.auth0Client.getTokenWithPopup(opt);
  }

  /** Logs the user out and removes their session on the authorization server */
  logout(opt: LogoutOptions) {
    if (TESTING_UTIL) {
      this.token = null;
    }

    const base = {
      /* eslint-disable-next-line camelcase */
      client_id: this.clientId,
      returnTo: config.acoHost,
    };

    const logoutOptions: LogoutOptions = opt
      ? {
          ...base,
          ...opt,
        }
      : base;

    return this.auth0Client.logout(logoutOptions);
  }

  async setAuthData() {
    // TESTING_UTIL is a global set by Webpack
    const auth0Data = TESTING_UTIL && window.localStorage.getItem('auth0Data');

    if (auth0Data) {
      // For e2e tests, use auth status that has been set in localStorage via Cypress
      const parsed = JSON.parse(auth0Data);
      this.isAuthenticated = true;
      this.user = parsed.user;
      this.token = parsed.access_token;
      this.idToken = parsed.id_token;
    } else {
      // Initialize our internal authentication state
      this.isAuthenticated = await this.auth0Client.isAuthenticated();
      this.user = await this.auth0Client.getUser();
      this.claims = await this.auth0Client.getIdTokenClaims();
    }
  }

  async init() {
    /** Use this lifecycle method to instantiate the SDK client */
    this.clientId = this.options.clientId;
    // Create a new instance of the SDK client using members of the given options object
    this.auth0Client = await createAuth0Client({
      domain: this.options.domain,
      // eslint-disable-next-line camelcase
      client_id: this.options.clientId,
      audience: this.options.audience,
      // eslint-disable-next-line camelcase
      redirect_uri: this.options.redirectUri,
      useRefreshTokens: true,
    });

    try {
      // If the user is returning to the app after authentication..
      if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
        // handle the redirect and retrieve tokens
        const result: { appState?: AppState } = await this.handleRedirectCallback();

        // Notify subscribers that the redirect callback has happened, passing the appState
        // (useful for retrieving any pre-authentication state)
        if (result && result.appState) {
          this.options.onRedirectCallback(result.appState);
          if (result.appState.targetUrl) {
            this.targetUrl = result.appState.targetUrl;
          }
        }
      }
    } catch (err) {
      console.error(err);
      this.error = err;
    } finally {
      await this.setAuthData();

      this.loading = false;

      this.loadListeners.forEach((fn) => fn());
    }
  }
}

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const getAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}: AuthOptions) => {
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new AuthPlugin({
    onRedirectCallback,
    redirectUri,
    ...options,
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin: Plugin = {
  install(app, options) {
    const auth0 = getAuth0(options);

    // options API
    app.config.globalProperties.$auth = auth0;

    // composition API
    app.provide('$auth', auth0);
  },
};
